// v3.11.0 — Carte fin de résultats annuaire : "Tu veux me donner plus de
// détails ?" Oui/Non → formulaire besoin (texte + vocal SpeechRecognition
// fr-FR + niveau d'urgence) → POST /api/v3/leads (reformulation LLM serveur
// + routage vers les dashboards pros correspondants).

import { useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const URGENCIES = [
  { id: "faible", label: "🟢 Quand possible" },
  { id: "moyenne", label: "🟡 Cette semaine" },
  { id: "urgente", label: "🔴 Urgent" },
];

export default function DetailPromptCard({ data }) {
  const promptId = data?.prompt_id || `${data?.domain}-${data?.category_id}`;
  const lsKey = `aya_lead_${promptId}`;
  const [step, setStep] = useState(() => {
    try { return localStorage.getItem(lsKey) || "ask"; } catch { return "ask"; }
  });
  const [text, setText] = useState("");
  const [urgency, setUrgency] = useState("moyenne");
  const [listening, setListening] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);

  const persist = (s) => { setStep(s); try { localStorage.setItem(lsKey, s); } catch {} };

  const toggleMic = () => {
    if (listening) { try { recRef.current?.stop(); } catch {} setListening(false); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setError("Micro non supporté sur ce navigateur — écris ta demande."); return; }
    const rec = new SR();
    rec.lang = "fr-FR";
    rec.interimResults = false;
    rec.continuous = true;
    rec.onresult = (e) => {
      let t = "";
      for (let i = e.resultIndex; i < e.results.length; i++) t += e.results[i][0].transcript;
      if (t) setText((prev) => (prev ? prev + " " : "") + t.trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    try { rec.start(); setListening(true); setError(null); } catch { setListening(false); }
  };

  const submit = async () => {
    const desc = text.trim();
    if (desc.length < 5) { setError("Décris ton besoin en quelques mots (5 caractères min)."); return; }
    setSending(true); setError(null);
    try {
      const token = localStorage.getItem("civ_user_token");
      const r = await fetch(`${API}/api/v3/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          domain: data.domain,
          category_id: data.category_id,
          category_label: data.category_label,
          description: desc,
          urgency,
          user_uuid: localStorage.getItem("jsonfirst_civ_userid") || undefined,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) throw new Error(d.error || "envoi impossible");
      persist("done");
    } catch (e) {
      setError("Envoi impossible — réessaie dans un instant.");
    } finally { setSending(false); }
  };

  const box = {
    marginTop: 10, borderRadius: 14, padding: "14px 16px",
    background: "linear-gradient(135deg, rgba(16,185,129,0.10), rgba(11,69,49,0.35))",
    border: "1px solid rgba(16,185,129,0.35)", color: "#F4F1E8",
  };

  if (step === "no") {
    return (
      <div data-testid="detail-prompt-declined" style={box}>
        <span style={{ fontSize: 13, opacity: 0.8 }}>Ça marche 👍 Si tu changes d'avis, redemande-moi quand tu veux.</span>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div data-testid="lead-form-success" style={box}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>✅ Demande transmise !</div>
        <div style={{ fontSize: 12.5, opacity: 0.85 }}>
          J'ai reformulé ton besoin et je l'ai envoyé aux professionnels « {data.category_label} » concernés. Ils te contacteront directement 💚
        </div>
      </div>
    );
  }

  if (step === "form") {
    return (
      <div data-testid="lead-form-card" style={box}>
        <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 8 }}>
          🗣️ Décris ton besoin — je transmets aux {data.category_label || "professionnels"}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <textarea
            data-testid="lead-form-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ex : je cherche un cardiologue pour ma maman, elle a des palpitations depuis 2 jours…"
            rows={3}
            style={{
              flex: 1, resize: "vertical", borderRadius: 10, padding: "10px 12px", fontSize: 13,
              background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.15)", color: "#F4F1E8", outline: "none",
            }}
          />
          <button
            type="button"
            data-testid="lead-form-mic"
            onClick={toggleMic}
            title={listening ? "Arrêter le micro" : "Dicter à la voix"}
            style={{
              width: 44, height: 44, borderRadius: 999, border: "none", cursor: "pointer", fontSize: 18,
              background: listening ? "linear-gradient(135deg,#EF4444,#B91C1C)" : "rgba(255,255,255,0.10)",
              color: "#fff", animation: listening ? "pulse 1.2s infinite" : "none",
            }}
          >{listening ? "⏹" : "🎙"}</button>
        </div>
        {listening && <div style={{ fontSize: 11.5, color: "#34D399", marginTop: 6 }}>🎙 J'écoute… parle normalement, j'écris pour toi.</div>}
        <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
          {URGENCIES.map((u) => (
            <button
              key={u.id}
              type="button"
              data-testid={`lead-urgency-${u.id}`}
              onClick={() => setUrgency(u.id)}
              style={{
                borderRadius: 999, padding: "6px 12px", fontSize: 12, cursor: "pointer",
                border: urgency === u.id ? "1px solid #10B981" : "1px solid rgba(255,255,255,0.15)",
                background: urgency === u.id ? "rgba(16,185,129,0.25)" : "rgba(255,255,255,0.05)",
                color: "#F4F1E8", fontWeight: urgency === u.id ? 700 : 400,
              }}
            >{u.label}</button>
          ))}
        </div>
        {error && <div data-testid="lead-form-error" style={{ fontSize: 12, color: "#FCA5A5", marginTop: 8 }}>{error}</div>}
        <button
          type="button"
          data-testid="lead-form-submit"
          onClick={submit}
          disabled={sending}
          style={{
            marginTop: 12, width: "100%", borderRadius: 999, padding: "10px 0", border: "none", cursor: "pointer",
            background: "linear-gradient(135deg,#34D399,#10B981)", color: "#062A1F", fontWeight: 800, fontSize: 13.5,
            opacity: sending ? 0.6 : 1,
          }}
        >{sending ? "Envoi en cours…" : "📤 Envoyer aux professionnels"}</button>
      </div>
    );
  }

  // step === "ask"
  return (
    <div data-testid="detail-prompt-card" style={box}>
      <div style={{ fontSize: 13.5, marginBottom: 10 }}>
        💬 Tu veux me donner plus de détails pour que je te trouve <strong>exactement</strong> ce qu'il te faut ?
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          data-testid="detail-prompt-yes"
          onClick={() => setStep("form")}
          style={{
            flex: 1, borderRadius: 999, padding: "9px 0", border: "none", cursor: "pointer",
            background: "linear-gradient(135deg,#34D399,#10B981)", color: "#062A1F", fontWeight: 800, fontSize: 13,
          }}
        >Oui, je précise 🎙</button>
        <button
          type="button"
          data-testid="detail-prompt-no"
          onClick={() => persist("no")}
          style={{
            flex: 1, borderRadius: 999, padding: "9px 0", cursor: "pointer",
            background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "#F4F1E8", fontSize: 13,
          }}
        >Non merci</button>
      </div>
    </div>
  );
}
